import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { ExamFormItem, SectionModule, SectionQuizResult } from '../core/models';
import { MarkdownPipe } from '../shared/markdown.pipe';

type Phase = 'module' | 'quiz' | 'result';

/**
 * VAR-agent certification — section page (Task 8, AB#1827 follow-on): module
 * content -> inline 5-question quiz -> inline result, all on one route so a
 * taker never leaves `/certification/section/:section`.
 *
 * `:section` is the domain slug — `CertificationHubComponent`'s cards link
 * here with `s.domain` (never `s.code`), and it is passed straight through
 * to all three `SupportApiService` section methods unchanged.
 *
 * Folds in what was originally a separate "quiz" step (Task 9) — the quiz
 * has no standalone URL of its own, it only ever exists mid-flow on this
 * page, so building it as one component avoids a throwaway intermediate
 * route.
 */
@Component({
  selector: 'app-section',
  standalone: true,
  imports: [CommonModule, RouterLink, NgbTooltip, MarkdownPipe],
  template: `
    <div class="bt-page section-page">
      <!-- Always-present return to the hub. The module ("Review") and quiz phases had no
           way back before — only the result phase did — so a taker who opened a section to
           review was stuck without the browser Back button. -->
      <a class="section-back" routerLink="/certification" data-testid="section-back"
         ngbTooltip="Back to all certification sections">← Certification</a>

      <!-- Shared across phases: startQuiz() (Start on the module, or Retake from the
           fail-result panel) can fail from EITHER phase, so its error can't live inside
           a single phase's @if block or a retake failure renders nothing. -->
      @if (quizError) {
        <p class="bt-badge bt-badge--danger" data-testid="section-quiz-start-error">{{ quizError }}</p>
      }

      @if (phase === 'module') {
        @if (loadingModule) {
          <div class="bt-empty"><span class="bt-spinner"></span><span class="bt-empty__hint">Loading section…</span></div>
        } @else if (moduleError) {
          <div class="bt-empty" data-testid="section-module-error">
            <span class="bt-empty__icon">⚠️</span>
            <span class="bt-empty__title">Couldn't load this section</span>
            <span class="bt-empty__hint">{{ moduleError }}</span>
            <button type="button" class="bt-btn bt-btn--secondary bt-btn--sm" data-testid="section-module-retry"
                    (click)="loadModule()">
              Try again
            </button>
          </div>
        } @else if (module) {
          <div class="bt-page__head">
            <h1 class="bt-page__title">{{ module.title }}</h1>
            @if (module.passed) {
              <span class="bt-badge bt-badge--success" data-testid="section-passed-badge"
                    ngbTooltip="You've already passed this section — you can review and retake any time">
                Passed ✓
              </span>
            }
          </div>

          <div class="bt-panel module-body md-body" data-testid="section-module-body"
               [innerHTML]="module.body_md | markdown"></div>

          <button type="button" class="bt-btn bt-btn--primary" data-testid="section-start-quiz"
                  [disabled]="startingQuiz" (click)="startQuiz()"
                  ngbTooltip="Take this section's 5-question quiz">
            {{ startingQuiz ? 'Starting…' : (module.passed ? 'Retake quiz' : 'Start quiz') }}
          </button>
        }
      }

      @if (phase === 'quiz') {
        <div class="bt-page__head">
          <h1 class="bt-page__title">{{ module?.title }}</h1>
          <span class="bt-badge bt-badge--neutral" data-testid="section-quiz-progress"
                ngbTooltip="This section's quiz is 5 questions">
            5 questions
          </span>
        </div>

        <div class="exam-items">
          @for (item of form; track item.item_id; let i = $index) {
            <div class="bt-panel exam-item" [attr.data-testid]="'section-item-' + item.item_id">
              <div class="exam-item__stem">
                <span class="exam-item__num">{{ i + 1 }}.</span>
                <span [innerHTML]="item.stem_md | markdown:true"></span>
              </div>
              <div class="exam-item__options">
                @for (opt of item.options; track opt.key) {
                  <label class="exam-option" [attr.data-testid]="'section-option-' + item.item_id + '-' + opt.key">
                    @if (item.item_type === 'single') {
                      <input type="radio" [name]="'section-item-' + item.item_id"
                             [checked]="isSelected(item.item_id, opt.key)"
                             (change)="selectSingle(item.item_id, opt.key)"
                             [attr.data-testid]="'section-input-' + item.item_id + '-' + opt.key"
                             ngbTooltip="Select this answer" />
                    } @else {
                      <input type="checkbox"
                             [checked]="isSelected(item.item_id, opt.key)"
                             (change)="toggleMulti(item.item_id, opt.key, $event)"
                             [attr.data-testid]="'section-input-' + item.item_id + '-' + opt.key"
                             ngbTooltip="Toggle this answer — this question accepts more than one" />
                    }
                    <span>{{ opt.text }}</span>
                  </label>
                }
              </div>
            </div>
          }
        </div>

        @if (submitError) {
          <p class="bt-badge bt-badge--danger" data-testid="section-submit-error">{{ submitError }}</p>
        }
        <button type="button" class="bt-btn bt-btn--primary" data-testid="section-submit-quiz"
                [disabled]="submitting" (click)="submit()"
                ngbTooltip="Submit your answers for grading">
          {{ submitting ? 'Submitting…' : 'Submit' }}
        </button>
      }

      @if (phase === 'result' && result) {
        @if (result.passed) {
          <div class="bt-panel verdict verdict--passed" data-testid="section-result-pass">
            <div class="ring" role="img" [attr.aria-label]="correctCount() + ' of ' + totalQuestions() + ' correct'">
              <svg viewBox="0 0 96 96" width="96" height="96">
                <circle class="ring__track" cx="48" cy="48" r="42" fill="none" stroke-width="8"></circle>
                <circle class="ring__value" cx="48" cy="48" r="42" fill="none" stroke-width="8"
                        stroke-linecap="round" transform="rotate(-90 48 48)"
                        [attr.stroke-dasharray]="circumference"
                        [attr.stroke-dashoffset]="dashOffset()"></circle>
              </svg>
              <div class="ring__label">
                <span class="ring__score" data-testid="section-result-score">{{ correctCount() }}<span class="ring__of">/{{ totalQuestions() }}</span></span>
              </div>
            </div>
            <div class="verdict__body">
              <span class="bt-badge bt-badge--success verdict__badge">Passed ✓</span>
              <h2 class="verdict__headline" data-testid="section-result-headline">Section passed ✓</h2>
              @if (result.certified) {
                <p class="verdict__reason" data-testid="section-certified-message">
                  You've passed every section — you're certified 🎉
                </p>
                <div class="verdict__actions">
                  <a class="bt-btn bt-btn--primary" routerLink="/inbox" data-testid="section-go-inbox"
                     ngbTooltip="You're certified — open the queue inbox">
                    Go to Inbox
                  </a>
                </div>
              } @else {
                <p class="verdict__reason">{{ result.passed_count }} of {{ result.total }} sections passed so far.</p>
                <div class="verdict__actions">
                  <a class="bt-btn bt-btn--primary" routerLink="/certification" data-testid="section-next"
                     ngbTooltip="Back to the section list">
                    Next section
                  </a>
                </div>
              }
            </div>
          </div>
        } @else {
          <div class="bt-panel verdict verdict--below" data-testid="section-result-fail">
            <div class="ring" role="img" [attr.aria-label]="correctCount() + ' of ' + totalQuestions() + ' correct'">
              <svg viewBox="0 0 96 96" width="96" height="96">
                <circle class="ring__track" cx="48" cy="48" r="42" fill="none" stroke-width="8"></circle>
                <circle class="ring__value" cx="48" cy="48" r="42" fill="none" stroke-width="8"
                        stroke-linecap="round" transform="rotate(-90 48 48)"
                        [attr.stroke-dasharray]="circumference"
                        [attr.stroke-dashoffset]="dashOffset()"></circle>
              </svg>
              <div class="ring__label">
                <span class="ring__score" data-testid="section-result-score">{{ correctCount() }}<span class="ring__of">/{{ totalQuestions() }}</span></span>
              </div>
            </div>
            <div class="verdict__body">
              <span class="bt-badge bt-badge--danger verdict__badge">Not passed</span>
              <h2 class="verdict__headline" data-testid="section-result-headline">Not passed — {{ correctCount() }}/{{ totalQuestions() }}</h2>

              @if (result.missed.length) {
                <div class="missed" data-testid="section-missed">
                  <p class="missed__title">Review these topics:</p>
                  <ul class="missed__list">
                    @for (m of result.missed; track m.code) {
                      <li [attr.data-testid]="'section-missed-' + m.code">{{ m.title }}</li>
                    }
                  </ul>
                </div>
              }

              <div class="verdict__actions">
                <button type="button" class="bt-btn bt-btn--primary" data-testid="section-retake"
                        [disabled]="startingQuiz" (click)="retake()"
                        ngbTooltip="Get a fresh set of 5 questions and try again">
                  {{ startingQuiz ? 'Starting…' : 'Retake' }}
                </button>
              </div>
            </div>
          </div>
        }
      }
    </div>
  `,
  styles: [`
    .section-page { display: flex; flex-direction: column; gap: 1rem; max-width: 44rem; }

    /* Back-to-hub link — sits above every phase. */
    .section-back {
      align-self: flex-start; font-size: 13px; font-weight: 600; text-decoration: none;
      color: var(--bt-brand-intercept); padding: 0.15rem 0;
    }
    .section-back:hover { text-decoration: underline; }
    .section-back:focus-visible { outline: 2px solid var(--bt-border-focus); outline-offset: 2px; border-radius: 3px; }

    /* Module content */
    .module-body { padding: 1.25rem 1.5rem; }

    /* Rendered-markdown formatting (shared shape for any .md-body). */
    .md-body :first-child { margin-top: 0; }
    .md-body :last-child { margin-bottom: 0; }
    .md-body h1, .md-body h2, .md-body h3 {
      font-family: 'Montserrat', sans-serif; color: var(--bt-text-primary);
      margin: 1rem 0 0.5rem; line-height: 1.3;
    }
    .md-body h1 { font-size: 18px; } .md-body h2 { font-size: 16px; } .md-body h3 { font-size: 14px; }
    .md-body p { margin: 0 0 0.6rem; color: var(--bt-text-secondary); line-height: 1.6; }
    .md-body ul, .md-body ol { margin: 0 0 0.6rem; padding-left: 1.35rem; }
    .md-body li { margin: 0.15rem 0; color: var(--bt-text-secondary); }
    .md-body strong { color: var(--bt-text-primary); font-weight: 600; }
    .md-body code {
      font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 0.9em;
      background: var(--bt-bg-surface-2); padding: 0.1em 0.35em; border-radius: 4px;
    }
    .md-body pre { background: var(--bt-bg-surface-2); padding: 0.75rem; border-radius: 6px; overflow-x: auto; }
    .md-body pre code { background: none; padding: 0; }
    .md-body a { color: var(--bt-brand-intercept); }
    .md-body blockquote {
      margin: 0 0 0.6rem; padding-left: 0.85rem; border-left: 3px solid var(--bt-border-light);
      color: var(--bt-text-muted);
    }

    /* Quiz (mirrors ExamComponent's item/option markup) */
    .exam-items { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1rem; }
    .exam-item { padding: 1rem 1.25rem; }
    .exam-item__stem { font-weight: 500; color: var(--bt-text-primary); margin-bottom: 0.6rem; }
    .exam-item__num { color: var(--bt-text-muted); margin-right: 0.3rem; }
    .exam-item__options { display: flex; flex-direction: column; gap: 0.4rem; }
    .exam-option { display: flex; align-items: center; gap: 0.5rem; font-size: 14px; color: var(--bt-text-secondary); cursor: pointer; }
    .exam-option input { accent-color: var(--bt-brand-intercept); cursor: pointer; }

    /* Result — score ring + verdict (scaled-down ResultComponent styling) */
    .verdict { display: flex; gap: 1.5rem; align-items: center; padding: 1.75rem 2rem; position: relative; overflow: hidden; }
    .verdict::before {
      content: ''; position: absolute; inset: 0; opacity: 0.06; pointer-events: none;
      background: radial-gradient(360px 170px at 12% -20%, var(--accent), transparent 70%);
    }
    .verdict--passed { --accent: var(--bt-status-success); }
    .verdict--below { --accent: var(--bt-status-danger); }

    .ring { position: relative; flex: 0 0 auto; width: 96px; height: 96px; }
    .ring__track { stroke: var(--bt-border-light); }
    .ring__value { stroke: var(--accent); transition: stroke-dashoffset .8s cubic-bezier(.22,1,.36,1); }
    .ring__label { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
    .ring__score { font-family: 'Montserrat', sans-serif; font-size: 24px; font-weight: 700; line-height: 1; color: var(--bt-text-primary); }
    .ring__of { font-size: 13px; font-weight: 600; color: var(--bt-text-secondary); margin-left: 1px; }

    .verdict__body { flex: 1; min-width: 0; }
    .verdict__badge { font-size: 12px; }
    .verdict__headline { font-family: 'Montserrat', sans-serif; font-size: 1.2rem; font-weight: 700; margin: 0.5rem 0 0.35rem; color: var(--bt-text-primary); }
    .verdict__reason { margin: 0; color: var(--bt-text-secondary); line-height: 1.55; }
    .verdict__actions { display: flex; align-items: center; gap: 0.75rem; margin-top: 1rem; flex-wrap: wrap; }

    .missed { margin-top: 0.75rem; }
    .missed__title { margin: 0 0 0.3rem; font-size: 13px; font-weight: 600; color: var(--bt-text-primary); }
    .missed__list { margin: 0; padding-left: 1.2rem; color: var(--bt-text-secondary); font-size: 14px; }
    .missed__list li { margin: 0.15rem 0; }

    @media (max-width: 560px) {
      .verdict { flex-direction: column; text-align: center; }
      .verdict__actions { justify-content: center; }
    }
  `],
})
export class SectionComponent implements OnInit {
  private api = inject(SupportApiService);
  private route = inject(ActivatedRoute);

  section = '';
  phase: Phase = 'module';

  module: SectionModule | null = null;
  loadingModule = true;
  moduleError: string | null = null;

  attemptId: string | null = null;
  form: ExamFormItem[] = [];
  answers: Record<string, string[]> = {};
  startingQuiz = false;
  quizError: string | null = null;
  submitting = false;
  submitError: string | null = null;

  result: SectionQuizResult | null = null;

  readonly circumference = 2 * Math.PI * 42; // r=42 in the (scaled) ring SVG above

  ngOnInit(): void {
    this.section = this.route.snapshot.paramMap.get('section') ?? '';
    this.loadModule();
  }

  loadModule(): void {
    this.loadingModule = true;
    this.moduleError = null;
    this.api.getSectionModule(this.section).subscribe({
      next: (module) => { this.module = module; this.loadingModule = false; },
      error: () => {
        this.loadingModule = false;
        this.moduleError = 'Could not load this section. Try again.';
      },
    });
  }

  startQuiz(): void {
    if (this.startingQuiz) return;
    this.startingQuiz = true;
    this.quizError = null;
    this.api.startSectionQuiz(this.section).subscribe({
      next: (start) => {
        this.attemptId = start.attempt_id;
        this.form = start.form;
        this.answers = {};
        this.submitError = null;
        this.result = null;
        this.startingQuiz = false;
        this.phase = 'quiz';
      },
      error: () => {
        this.startingQuiz = false;
        this.quizError = 'Could not start the quiz. Try again.';
      },
    });
  }

  /** Fetches a fresh 5-question form and returns to the quiz phase — used by
   * the fail panel's Retake button. Same call as the initial Start; a new
   * attempt always means a new frozen form. */
  retake(): void {
    this.startQuiz();
  }

  isSelected(itemId: string, key: string): boolean {
    return (this.answers[itemId] ?? []).includes(key);
  }

  selectSingle(itemId: string, key: string): void {
    this.answers = { ...this.answers, [itemId]: [key] };
  }

  toggleMulti(itemId: string, key: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const current = this.answers[itemId] ?? [];
    const next = checked ? [...current, key] : current.filter((k) => k !== key);
    this.answers = { ...this.answers, [itemId]: next };
  }

  submit(): void {
    if (!this.attemptId || this.submitting) return;
    this.submitting = true;
    this.submitError = null;
    this.api.submitSectionQuiz(this.section, this.attemptId, this.answers).subscribe({
      next: (result) => {
        this.submitting = false;
        this.result = result;
        this.phase = 'result';
      },
      error: () => {
        this.submitting = false;
        this.submitError = 'Could not submit the quiz. Try again.';
      },
    });
  }

  totalQuestions(): number {
    return this.form.length || 5;
  }

  correctCount(): number {
    return this.result ? Math.round(this.result.score * this.totalQuestions()) : 0;
  }

  dashOffset(): number {
    const frac = this.result ? Math.max(0, Math.min(1, this.result.score)) : 0;
    return this.circumference * (1 - frac);
  }
}
